/**
 * Cart popup content component
 *
 * @since 1.0.0
 */
import { __ } from '@wordpress/i18n';

import { formatPrice, formatRegularPrice } from '../../utils/price-formatter';
import type { CartPopupContentProps } from './types';

/**
 * Cart popup content component
 */
export const CartPopupContent = ({
  product,
  cartInfo,
  cartData,
  onClose,
  displayMode = 'just_added',
}: CartPopupContentProps) => {
  // Early return if product is not provided
  if (!product) {
    return (
      <div className='redfox-added-to-cart-popup__cart-content'>
        <div className='redfox-added-to-cart-popup__error-message'>
          {__('Product information is not available.', 'redfox-added-to-cart-popup')}
        </div>
      </div>
    );
  }

  // Determine which products to display based on display mode
  const productsToShow = (() => {
    switch (displayMode) {
      case 'full_cart':
        // Show all products in cart if available
        if (cartData?.products && cartData.products.length > 0) {
          return cartData.products;
        }
        // Fallback to just the added product if no cart data
        return [product];
      case 'just_added':
      default:
        // Show only the just added product (use just_added_products if available)
        if (cartData?.just_added_products && cartData.just_added_products.length > 0) {
          return cartData.just_added_products;
        }
        // Fallback to the passed product
        return [product];
    }
  })();

  const isMultipleProducts = productsToShow.length > 1;

  const handleViewCart = () => {
    if (cartInfo?.cart_url) {
      window.location.href = cartInfo.cart_url;
    }
  };

  const handleCheckout = () => {
    if (cartInfo?.checkout_url) {
      window.location.href = cartInfo.checkout_url;
    }
  };

  const handleContinueShopping = () => {
    onClose();
  };

  const formatVariationName = (productItem: typeof product) => {
    if (!productItem?.is_variation || !productItem?.variation_data?.attributes) {
      return null;
    }

    try {
      return productItem.variation_data.attributes
        .map(
          (attr: { label?: string; formatted_value?: string; value?: string }) =>
            `${attr?.label ?? 'Attribute'}: ${attr?.formatted_value ?? attr?.value ?? ''}`
        )
        .filter(Boolean)
        .join(', ');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error formatting variation name:', error);
      return null;
    }
  };

  /**
   * Render a single product item
   */
  const renderProduct = (productItem: typeof product, index: number) => {
    // Provide default values for potentially missing properties
    const productName = productItem?.name ?? __('Unknown Product', 'redfox-added-to-cart-popup');
    const productImage = productItem?.image_url ?? '';
    const productImageAlt = productItem?.image_alt ?? productItem?.name ?? productName;
    const productPrice = productItem?.formatted_price ?? productItem?.price ?? '0';
    const productRegularPrice = productItem?.regular_price ?? productItem?.price ?? '0';
    const productQuantity = productItem?.quantity ?? 1;
    const isOnSale =
      productItem?.is_on_sale &&
      productItem?.regular_price &&
      productItem?.regular_price !== (productItem?.sale_price ?? productItem?.price);

    return (
      <div
        key={`product-${productItem.id}-${index}`}
        className='redfox-added-to-cart-popup__product'
      >
        <div className='redfox-added-to-cart-popup__product-image'>
          {productImage ? (
            <img src={productImage} alt={productImageAlt} loading='lazy' />
          ) : (
            <div className='redfox-added-to-cart-popup__no-image'>
              {__('No image', 'redfox-added-to-cart-popup')}
            </div>
          )}
        </div>

        <div className='redfox-added-to-cart-popup__product-details'>
          <h3 className='redfox-added-to-cart-popup__product-name'>{productName}</h3>

          <div className='redfox-added-to-cart-popup__product-price'>
            {isOnSale ? (
              <>
                <del className='redfox-added-to-cart-popup__regular-price'>
                  {formatRegularPrice(productRegularPrice, productPrice)}
                </del>
                <span className='redfox-added-to-cart-popup__sale-price'>
                  {formatPrice(productPrice)}
                </span>
              </>
            ) : (
              <span className='redfox-added-to-cart-popup__current-price'>
                {formatPrice(productPrice)}
              </span>
            )}
          </div>

          {/* Variation attributes */}
          {formatVariationName(productItem) && (
            <div className='redfox-added-to-cart-popup__product-attributes'>
              {formatVariationName(productItem)}
            </div>
          )}

          {/* Quantity if greater than 1 */}
          {productQuantity > 1 && (
            <div className='redfox-added-to-cart-popup__product-quantity'>
              {__('Quantity', 'redfox-added-to-cart-popup')}: {productQuantity}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className='redfox-added-to-cart-popup__cart-content'>
      {/* Success message */}
      <div className='redfox-added-to-cart-popup__success-message'>
        <span className='redfox-added-to-cart-popup__success-icon'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
            strokeWidth={1.5}
            stroke='currentColor'
            width={16}
            height={16}
          >
            <path strokeLinecap='round' strokeLinejoin='round' d='m4.5 12.75 6 6 9-13.5' />
          </svg>
        </span>
        {isMultipleProducts
          ? __('Products successfully added to your cart!', 'redfox-added-to-cart-popup')
          : __('Product successfully added to your cart!', 'redfox-added-to-cart-popup')}
      </div>

      {/* Product details */}
      <div
        className={`redfox-added-to-cart-popup__products ${isMultipleProducts ? 'redfox-added-to-cart-popup__products--multiple' : ''}`}
      >
        {productsToShow.map((productItem, index) => renderProduct(productItem, index))}
      </div>

      {/* Action buttons */}
      <div className='redfox-added-to-cart-popup__actions'>
        <button
          className='redfox-added-to-cart-popup__btn redfox-added-to-cart-popup__btn--view-cart'
          onClick={handleViewCart}
        >
          {__('View Cart', 'redfox-added-to-cart-popup')}
        </button>

        <button
          className='redfox-added-to-cart-popup__btn redfox-added-to-cart-popup__btn--checkout'
          onClick={handleCheckout}
        >
          {__('Checkout', 'redfox-added-to-cart-popup')}
        </button>

        <button
          className='redfox-added-to-cart-popup__btn redfox-added-to-cart-popup__btn--continue'
          onClick={handleContinueShopping}
        >
          {__('Continue Shopping', 'redfox-added-to-cart-popup')}
        </button>
      </div>
    </div>
  );
};
